import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCreditCardDetail, getChargesForCreditCard } from '../../services/dataService';
import { CreditCardDetail, Charge } from '../../types';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumbs from '../../components/Breadcrumbs';
import ShareButton from '../../components/ShareButton';
import AdComponent from '../../components/AdComponent';

const CreditCardDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [card, setCard] = useState<CreditCardDetail | null>(null);
    const [charges, setCharges] = useState<Charge[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (slug) {
            setLoading(true);
            setCard(null);
            setCharges([]);
            getCreditCardDetail(slug).then(cardData => {
                setCard(cardData || null);
                if (cardData) {
                    getChargesForCreditCard(cardData.id).then(chargeData => {
                        setCharges(chargeData);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            });
        }
    }, [slug]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!card) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold">Credit Card not found</h2>
                <Link to="/credit-card-charges" className="text-[#246BFD] hover:underline mt-4 inline-block">
                    &larr; Back to all credit cards
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6 max-w-4xl">
                <Breadcrumbs />
                <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900">{card.name}</h1>
                    </div>
                    <ShareButton
                        title={`All about ${card.name} fees | ChargesDecoder`}
                        text={`Find all fee details for ${card.name} on ChargesDecoder.`}
                        className="mt-4 sm:mt-0"
                    />
                </header>

                <main>
                    <AdComponent />
                    <h2 className="text-2xl font-bold font-poppins text-gray-800 mb-6">Common Fees & Charges</h2>
                    <div className="bg-white rounded-lg shadow-md border border-gray-200">
                        <ul className="divide-y divide-gray-200">
                           {charges.map(charge => (
                                <li key={charge.id}>
                                    <Link to={`/charges/${charge.id}`} className="block p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-800">{charge.fullName} <span className="text-gray-500 font-normal">({charge.name})</span></h3>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{charge.whatItMeans}</p>
                                            </div>
                                            <div className="flex flex-row items-center gap-4 flex-shrink-0 self-start sm:self-center">
                                                <p className="font-semibold text-[#246BFD] text-lg font-roboto-mono text-left sm:text-right">{charge.typicalAmount}</p>
                                                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                         {charges.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No specific fees listed for this card in our main database yet.
                            </div>
                        )}
                    </div>

                    {/* New Section for Foreign Transaction Fees */}
                    <div className="mt-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
                        <div className="flex items-center mb-3 text-blue-800">
                            <span className="mr-3 text-[#246BFD]">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9V3m0 18a9 9 0 00-9-9m-9 9a9 9 0 019-9" /></svg>
                            </span>
                            <h2 className="text-xl font-bold font-poppins">Pro Tip: Understanding Foreign Transaction Fees</h2>
                        </div>
                        <div className="text-blue-700 space-y-3 text-sm">
                            <p>Most standard credit cards charge a <strong className="font-semibold">Foreign Transaction (Forex) Fee</strong> on all purchases made in a foreign currency. This fee, typically <strong className="font-semibold">1.75% to 3.5%</strong> of the transaction amount, applies when you travel abroad or shop on international websites.</p>
                            <h3 className="font-semibold text-blue-800 text-base">How to Save Money:</h3>
                            <ul className="list-disc list-inside space-y-1.5 pl-2">
                                <li><strong className="font-semibold">Use a Zero Forex Card:</strong> Look for specialized travel credit cards that advertise 'zero' or 'low' forex markup fees. These can save you a significant amount on international spending.</li>
                                <li><strong className="font-semibold">Pay in Local Currency:</strong> When using your card abroad, if the machine asks to pay in your home currency or the local currency, <strong className="font-semibold">always choose the local currency</strong>. This helps you avoid high 'Dynamic Currency Conversion' (DCC) rates set by the merchant's bank.</li>
                                <li><strong className="font-semibold">Consider Fintech Services:</strong> For online purchases or sending money, services like Wise or Revolut often offer better exchange rates and lower fees than traditional credit cards.</li>
                            </ul>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CreditCardDetailPage;